'use client';
import React, { ComponentProps, useEffect, useMemo, useState } from 'react';
import { Model, Platform } from '@/packages/sxm/ymm/types';
import { PLATFORM } from '@/constants';
import dynamic from 'next/dynamic';
// Import ModalTYmmModalemplate dynamically because it includes browser-specific libs(e.g. focus-trap-react)
const YmmModal = dynamic(() => import('@/ui-components/molecules/YmmModal/YmmModal'), { ssr: false });
import { getYMMCookies, setYMMCookies } from '@/packages/sxm/ymm';
import { useYmmSelector } from './useYmmSelector';
import { determinePlatformFromCapabilities } from './useVehiclePlatformSync';

interface PortableRadio {
    radio: string;
    platform: Platform;
}

export const useYmm20 = () => {
    const {
        ymmData: ymm,
        ymmOptions,
        loadYears: getYears,
        loadSelectionOptions,
        setSelection,
        selectYear,
        selectMake,
        selectModel,
    } = useYmmSelector();
    const [ymmTextBar, setYmmTextBar] = useState<string>('Streaming & Most Radios');
    const [portableRadio, setPortable] = useState<PortableRadio>({ radio: '', platform: PLATFORM.SIRIUSXM });
    const [typeSelected, setTypeSelected] = useState<string>('online');

    useEffect(() => {
        const ymmCookies = getYMMCookies();
        const carData = ymmCookies.sxm_radio.split('|');
        const ymmBar = ymmCookies.ymm_bar.toLowerCase();

        if (carData.length > 1) {
            const cookieSelection = {
                year: carData[1],
                make: carData[2],
                model: carData[3],
                id: carData[4],
            };
            setSelection(cookieSelection);
            void loadSelectionOptions(cookieSelection.year, cookieSelection.make);
            setYmmTextBar(`${cookieSelection.year} ${cookieSelection.make} ${cookieSelection.model}`);
            setTypeSelected('car');
        } else {
            setYmmTextBar(ymmCookies.ymm_bar || 'Streaming & Most Radios');
            if (ymmBar.indexOf('streaming') >= 0) {
                setTypeSelected('online');
            } else if (ymmBar.indexOf('radio') >= 0) {
                setTypeSelected('radio');
            }
        }

        ymmBar.split('|').forEach((cookie: string) => {
            if (cookie.includes('radio') && !cookie.includes('radios')) {
                setPortable({
                    radio: cookie,
                    platform: (ymmCookies.sxm_platform as Platform) || PLATFORM.SIRIUSXM,
                });
            }
        });

        function inlineClickHandler(e: MouseEvent) {
            let anchor: HTMLAnchorElement | null = e.target as HTMLAnchorElement;

            if (anchor?.tagName !== 'A') {
                anchor = anchor.closest('a');
            }

            if (anchor?.getAttribute('href') === '#ymmModal') {
                getYears();
                document.removeEventListener('click', inlineClickHandler);
            }
        }

        if (!ymmOptions.years.length) {
            document.addEventListener('click', inlineClickHandler);
        }

        return () => {
            document.removeEventListener('click', inlineClickHandler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const ymmModalProps: ComponentProps<typeof YmmModal> = useMemo(() => {
        const yearHandler = (_: HTMLInputElement | undefined, params: { value: string }) => {
            selectYear(params.value);
        };

        const makeHandler = (_: HTMLInputElement | undefined, params: { value: string }) => {
            selectMake(params.value);
        };

        const modelHandler = (_: HTMLInputElement | undefined, params: { value: string; data?: Model }) => {
            if (params.data) {
                selectModel(params.data);
            }
        };

        const ymmFormProps = {
            yearHandler: yearHandler,
            makeHandler: makeHandler,
            modelHandler: modelHandler,
            ymmData: ymm,
            ymmOptions: ymmOptions,
        };

        const saveCookies: ComponentProps<typeof YmmModal>['onSubmit'] = (ymmData, radio, selectedType) => {
            let platform: Platform = determinePlatformFromCapabilities();
            let ymmText = '';

            if (selectedType === 'radio' && radio) {
                ymmText = radio.radio;
                platform = radio.platform;
            }

            if (selectedType === 'online') {
                ymmText = 'Streaming & Most Radios';
            }

            if (selectedType === 'car' && ymmData) {
                platform = determinePlatformFromCapabilities(ymmData.capabilities);
            }

            const cookieValue = {
                sxm_radio: selectedType === 'car' ? `YMM|${ymm.year}|${ymm.make}|${ymm.model}|${ymm.id}` : '',
                sxm_platform: platform,
                ymm_bar: ymmText,
                roadblockbypass: '',
                inft_ymm: selectedType === 'car' ? ymmData.capabilities?.infotainment.join('|') || '' : '',
            };

            setYMMCookies(cookieValue);
            setYmmTextBar(ymmText);

            if (window) {
                window.location.reload();
            }

            document.querySelector('html')?.classList.remove('showYmm');
        };

        return {
            onClose: () => {
                document.querySelector('html')?.classList.remove('showYmm');
            },
            portableValues: portableRadio,
            onSubmit: saveCookies,
            formProps: ymmFormProps,
            typeCheck: typeSelected,
        };
    }, [portableRadio, selectMake, selectModel, selectYear, typeSelected, ymm, ymmOptions]);

    return {
        ymmTextBar,
        modal: <YmmModal {...ymmModalProps} />,
        getYears,
    };
};
